import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  OfficeBuildingIcon,
  PencilIcon,
  UserIcon,
} from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import ExpandableDescription from "../ExpandableDescription";
import { listDeposits, useAuthState } from "../../../../Context";
import { formatCurrency } from "../../../../Utils";

const TYPE_STYLES = {
  PERSONAL: {
    badge: "bg-blue-100 text-blue-800",
    icon: UserIcon,
    label: "businesses.types.personal",
  },
  BUSINESS: {
    badge: "bg-amber-100 text-amber-800",
    icon: OfficeBuildingIcon,
    label: "businesses.types.business",
  },
};

const ContactDetailModal = ({ open, onClose, onEdit, partner }) => {
  const { t } = useTranslation();
  const { user } = useAuthState();
  const businessID = user?.mainBusiness?.id;

  // Deposits the customer has paid that are not used up yet. The card stays
  // hidden while there are none, and when they cannot be read.
  const [deposits, setDeposits] = useState(null);
  useEffect(() => {
    setDeposits(null);
    if (!open || !partner?.id || !businessID) return;
    let live = true;
    listDeposits({ businessID, partnerID: partner.id, status: "open" })
      .then(({ summary }) => {
        if (live) setDeposits(summary);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [open, partner?.id, businessID]);

  if (!partner) return null;

  const typeStyle = TYPE_STYLES[partner.type] || TYPE_STYLES.PERSONAL;
  const TypeIcon = typeStyle.icon;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("contacts.detail.title")}
      actionButton={
        onEdit && (
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => onEdit(partner)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header Summary */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                <TypeIcon className="h-6 w-6 text-gray-500" />
              </div>
              <div>
                <ExpandableDescription
                  text={partner.name}
                  maxLength={40}
                  textClassName="text-xl font-semibold text-gray-900"
                />
              </div>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${typeStyle.badge}`}
            >
              {t(typeStyle.label)}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("contacts.fields.npwp")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {partner.taxNumber ? partner.taxNumber : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("contacts.fields.email")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {partner.email ? (
                  <a
                    href={`mailto:${partner.email}`}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {partner.email}
                  </a>
                ) : (
                  "-"
                )}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("contacts.fields.phone")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {partner.phone ? (
                  <a
                    href={`tel:${partner.phone}`}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {partner.phone}
                  </a>
                ) : (
                  "-"
                )}
              </dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("contacts.fields.address")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {partner.address ? (
                  <ExpandableDescription
                    text={partner.address}
                    maxLength={80}
                  />
                ) : (
                  "-"
                )}
              </dd>
            </div>
          </dl>
        </div>

        {deposits?.remaining > 0 && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-purple-100 bg-purple-50 px-4 py-4 sm:px-6">
            <div className="min-w-0">
              <p className="text-sm font-medium text-purple-900">
                {t("deposits.contact.card")}
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-purple-950 [overflow-wrap:anywhere]">
                {formatCurrency(deposits.remaining)}
              </p>
              <p className="text-sm text-purple-800">
                {t("deposits.contact.count", { interpolation: { escapeValue: false }, count: deposits.count })}
              </p>
            </div>
            <Link
              to={`/v2/accounting/incomes?tab=deposits&partnerId=${encodeURIComponent(partner.id)}`}
              onClick={onClose}
              className="whitespace-nowrap text-sm font-medium text-blue-600 hover:underline"
            >
              {t("deposits.actions.seeDeposits")}
            </Link>
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default ContactDetailModal;
